import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PackageOpen, Plus, Search, X, List as ListIcon, Grid3x3, FileSpreadsheet, Pencil, Trash2, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { photoFull } from "@/lib/photoUrl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/** 寄样管理 — every 寄样清单 (shared), grid view by default, with search,
 *  customer filter and a 创建日期 range. */
export default function SampleLists() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("grid");
  const [q, setQ] = useState("");
  const [customer, setCustomer] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [customers, setCustomers] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newCustomer, setNewCustomer] = useState("");
  const [newReceipt, setNewReceipt] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const { data } = await api.get("/sample-lists", { params });
      setLists(data.items || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    api.get("/customers").then(({ data }) => setCustomers((data || []).map((c) => c.name).filter(Boolean))).catch(() => {});
  }, [load]);

  const applyFilters = () => load({ q, customer, date_from: from, date_to: to });
  const clearFilters = () => { setQ(""); setCustomer(""); setFrom(""); setTo(""); load(); };

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sample-lists", { name: newName.trim(), customer_name: newCustomer.trim(), receipt_no: newReceipt.trim() });
      setNewOpen(false); setNewName(""); setNewCustomer(""); setNewReceipt("");
      navigate(`/sample-lists/${data.id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const rename = async (l) => {
    const name = window.prompt(t("sample.renamePrompt"), l.name);
    if (!name || name === l.name) return;
    try { await api.patch(`/sample-lists/${l.id}`, { name }); applyFilters(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (l) => {
    if (!window.confirm(t("sample.deleteConfirm").replace("{name}", l.name))) return;
    try { await api.delete(`/sample-lists/${l.id}`); toast.success(t("sample.deleted")); applyFilters(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const meta = (l) => `${t("sample.count").replace("{n}", l.count)} · ${l.created_by} · ${(l.created_at || "").slice(0, 10)}`;

  return (
    <div className="animate-fade-in-up space-y-4" data-testid="sample-lists-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-base font-bold tracking-tight flex items-center gap-2">
            <PackageOpen size={18} className="text-brand" /> {t("sample.manage")}
          </h1>
          <p className="text-xs text-muted-foreground mt-1">{t("sample.pageHint")}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 border border-border rounded-md p-0.5">
            <Button variant={view === "list" ? "secondary" : "ghost"} size="icon" className="h-7 w-7"
              onClick={() => setView("list")} data-testid="sl-view-list"><ListIcon size={13} /></Button>
            <Button variant={view === "grid" ? "secondary" : "ghost"} size="icon" className="h-7 w-7"
              onClick={() => setView("grid")} data-testid="sl-view-grid"><Grid3x3 size={13} /></Button>
          </div>
          <Button size="sm" onClick={() => setNewOpen(true)} data-testid="sl-new-btn">
            <Plus size={14} /> {t("sample.newList")}
          </Button>
        </div>
      </div>

      {/* filters */}
      <div className="bg-card border border-border rounded-md p-3 flex flex-wrap items-end gap-3" data-testid="sl-filters">
        <div className="flex-1 min-w-[220px]">
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("sample.search")}</Label>
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && applyFilters()}
            placeholder={t("sample.searchPlaceholder")} className="h-9 text-sm mt-1" data-testid="sl-search" />
        </div>
        <div className="min-w-[200px]">
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("sample.customer")}</Label>
          <Input value={customer} list="sl-customers" onChange={(e) => setCustomer(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applyFilters()}
            placeholder={t("sample.customerAny")} className="h-9 text-sm mt-1" data-testid="sl-customer" />
          <datalist id="sl-customers">{customers.map((c) => <option key={c} value={c} />)}</datalist>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("sample.createdRange")}</Label>
          <div className="flex items-center gap-1 mt-1">
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 text-sm w-[150px]" data-testid="sl-from" />
            <span className="text-muted-foreground">–</span>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 text-sm w-[150px]" data-testid="sl-to" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" className="h-9" onClick={applyFilters} data-testid="sl-apply">
            <Search size={13} /> {t("sample.search")}
          </Button>
          <Button size="sm" variant="ghost" className="h-9" onClick={clearFilters} data-testid="sl-clear">
            <X size={13} /> {t("sample.clear")}
          </Button>
        </div>
      </div>

      {loading && <p className="text-center py-8"><Loader2 className="inline animate-spin text-muted-foreground" /></p>}

      {!loading && lists.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-12" data-testid="sl-empty">{t("sample.noLists")}</p>
      )}

      {!loading && view === "grid" && lists.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="sl-grid">
          {lists.map((l) => (
            <div key={l.id} className="border border-border rounded-md bg-card overflow-hidden hover:border-brand/50 hover:shadow-md transition-all"
              data-testid={`sl-card-${l.id}`}>
              <button type="button" onClick={() => navigate(`/sample-lists/${l.id}`)} className="w-full text-left"
                data-testid={`sl-open-${l.id}`}>
                <div className="grid grid-cols-2 gap-0.5 bg-secondary/40 aspect-[2/1]">
                  {(l.thumbs.length ? l.thumbs : [null, null, null, null]).slice(0, 4).map((u, i) => (
                    <div key={i} className="overflow-hidden">
                      {u && <img src={photoFull(u)} alt="" loading="lazy" className="w-full h-full object-cover" />}
                    </div>
                  ))}
                </div>
                <div className="p-3 space-y-1">
                  <p className="text-sm font-semibold truncate">{l.name}</p>
                  <p className="text-xs text-brand truncate">{l.customer_name || "—"}</p>
                  <p className="text-[11px] font-mono truncate" data-testid={`sl-receipt-${l.id}`}>{l.receipt_no || "—"}</p>
                  <p className="text-[11px] font-mono text-muted-foreground truncate">{meta(l)}</p>
                </div>
              </button>
              <div className="flex items-center justify-end gap-1 px-2 pb-2">
                <Button variant="ghost" size="icon" className="h-7 w-7" title={t("sample.export")}
                  onClick={() => window.open(`${BACKEND}/api/sample-lists/${l.id}/export`, "_blank")}><FileSpreadsheet size={13} /></Button>
                {l.can_manage && (
                  <>
                    <Button variant="ghost" size="icon" className="h-7 w-7" title={t("sample.rename")} onClick={() => rename(l)}><Pencil size={13} /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" title={t("sample.delete")} onClick={() => remove(l)}><Trash2 size={13} className="text-destructive" /></Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && view === "list" && lists.length > 0 && (
        <div className="bg-card border border-border rounded-md overflow-hidden">
          <table className="w-full text-xs" data-testid="sl-table">
            <thead className="bg-secondary/60">
              <tr className="text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 text-left">{t("sample.listName")}</th>
                <th className="px-3 py-2 text-left">{t("sample.customer")}</th>
                <th className="px-3 py-2 text-left w-32">{t("sample.receiptNo")}</th>
                <th className="px-3 py-2 text-left w-24">{t("sample.qtyProducts")}</th>
                <th className="px-3 py-2 text-left">{t("sample.createdBy")}</th>
                <th className="px-3 py-2 text-left w-28">{t("sample.createdAt")}</th>
                <th className="px-3 py-2 text-right w-32">{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {lists.map((l) => (
                <tr key={l.id} className="border-t border-border/60 hover:bg-secondary/40" data-testid={`sl-row-${l.id}`}>
                  <td className="px-3 py-2">
                    <button className="font-medium text-brand hover:underline" onClick={() => navigate(`/sample-lists/${l.id}`)}
                      data-testid={`sl-open-row-${l.id}`}>{l.name}</button>
                  </td>
                  <td className="px-3 py-2">{l.customer_name || "—"}</td>
                  <td className="px-3 py-2 font-mono">{l.receipt_no || "—"}</td>
                  <td className="px-3 py-2 font-mono">{l.count}</td>
                  <td className="px-3 py-2">{l.created_by}</td>
                  <td className="px-3 py-2 font-mono">{(l.created_at || "").slice(0, 10)}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" title={t("sample.export")}
                        onClick={() => window.open(`${BACKEND}/api/sample-lists/${l.id}/export`, "_blank")}><FileSpreadsheet size={13} /></Button>
                      {l.can_manage && (
                        <>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => rename(l)}><Pencil size={13} /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => remove(l)}><Trash2 size={13} className="text-destructive" /></Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={newOpen} onOpenChange={(o) => !o && setNewOpen(false)}>
        <DialogContent className="max-w-md" data-testid="sl-new-dialog">
          <DialogHeader><DialogTitle className="text-base">{t("sample.newList")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("sample.newName")}</Label>
              <Input value={newName} autoFocus onChange={(e) => setNewName(e.target.value)}
                placeholder={t("sample.namePlaceholder")} className="h-9 text-sm mt-1" data-testid="sl-new-name" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("sample.customer")}</Label>
              <Input value={newCustomer} list="sl-customers" onChange={(e) => setNewCustomer(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && create()}
                placeholder={t("sample.customerPlaceholder")} className="h-9 text-sm mt-1" data-testid="sl-new-customer" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("sample.receiptNo")}</Label>
              <Input value={newReceipt} onChange={(e) => setNewReceipt(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && create()}
                placeholder={t("sample.receiptNoPlaceholder")} className="h-9 text-sm mt-1" data-testid="sl-new-receipt" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setNewOpen(false)}>{t("market.cancel")}</Button>
            <Button size="sm" onClick={create} disabled={busy} data-testid="sl-new-submit">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} {t("sample.create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
